import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { isPhonePuzzleViewport } from '../config/telegramUi';

const CELL = {
    EMPTY: 0,
    FILLED: 1,
    MARKED: 2,
};

/** Shared vacant glyph — matches MARK tool icon. */
function MarkGlyph({ className = 'atelier-cell-x' }) {
    return (
        <svg className={className} viewBox="0 0 14 14" aria-hidden>
            <path
                d="M3.6 3.6 10.4 10.4M10.4 3.6 3.6 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
            />
        </svg>
    );
}

/** Focus layout: maximize square on phone; capped card fit on desktop. */
const CLUE_GUTTER = 30; // legacy / desktop-ish default for n>8 when not phone-dense
const CLUE_GUTTER_16_PORTRAIT = 22;
const CLUE_GUTTER_16_LANDSCAPE = 20;
const CLUE_GUTTER_COMPACT = 26;
const EDGE_PAD = 6;
const EDGE_PAD_PHONE = 2;
const TOOL_RAIL = 56;
const TOOL_ROW = 52;
const TOOL_ROW_DENSE = 40;
const STAGE_GAP = 2;
/** Desktop card: never chase the full window — keep a comfortable square. */
const DESKTOP_BOARD_CAP = {
  4: 300,
  8: 340,
  16: 360,
};

function desktopBoardCap(n) {
  if (n <= 4) return DESKTOP_BOARD_CAP[4];
  if (n <= 8) return DESKTOP_BOARD_CAP[8];
  return DESKTOP_BOARD_CAP[16];
}

function clueGutterFor(n, landscape, shortEdge, phone = shortEdge < 560) {
    if (n <= 8) return landscape && shortEdge < 560 ? 22 : CLUE_GUTTER_COMPACT;
    // Atelier 16×16: reclaim gutter px into cells on phone.
    if (phone || shortEdge < 560) {
        return landscape ? CLUE_GUTTER_16_LANDSCAPE : CLUE_GUTTER_16_PORTRAIT;
    }
    return CLUE_GUTTER;
}

function seedBoardPx(dim) {
    if (typeof window === 'undefined') return dim <= 8 ? 360 : 280;
    const vvW = window.visualViewport?.width ?? window.innerWidth;
    const vvH = window.visualViewport?.height ?? window.innerHeight;
    const shortEdge = Math.min(vvW, vvH);
    if (dim <= 8) return Math.min(360, Math.max(200, shortEdge - 40));
    return Math.min(360, Math.max(200, shortEdge - 50));
}

function cssVarPx(name) {
    try {
        const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
        const n = parseFloat(raw);
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
        return 0;
    }
}

const PuzzleGrid = ({
    clues,
    onGridChange,
    initialGrid,
    peekGrid = null,
    isInteractive = true,
    highlights = [],
    compactChrome = false,
    /** Phone landscape focus — size from viewport short edge when shell collapses. */
    immersiveLandscape = false,
    /** Desktop / wide Telegram: fit inside the card, do not maximize to the window. */
    desktopLayout = false,
    /** 'draw' | 'mark' | null — forces tool selection (guided tutorial). */
    paintMode = null,
}) => {
    const dim = initialGrid?.length || clues?.rows?.length || 16;
    const [grid, setGrid] = useState(
        initialGrid ||
            Array(dim)
                .fill(0)
                .map(() => Array(dim).fill(CELL.EMPTY))
    );
    const [mode, setMode] = useState(CELL.FILLED);
    const [boardPx, setBoardPx] = useState(() => seedBoardPx(dim));
    const [clueGutter, setClueGutter] = useState(() =>
        dim <= 8 ? CLUE_GUTTER_COMPACT : CLUE_GUTTER_16_PORTRAIT
    );
    const shellRef = useRef(null);
    const boardRef = useRef(null);
    const gridRef = useRef(grid);
    const isDragging = useRef(false);
    const dragPaintVal = useRef(null);
    const lastPainted = useRef(null);
    const lastNotified = useRef(null);

    gridRef.current = grid;

    useEffect(() => {
        if (paintMode === 'draw') setMode(CELL.FILLED);
        else if (paintMode === 'mark') setMode(CELL.MARKED);
    }, [paintMode]);

    useEffect(() => {
        if (peekGrid) return;
        const snap = JSON.stringify(grid);
        if (snap === lastNotified.current) return;
        lastNotified.current = snap;
        onGridChange?.(grid);
    }, [grid, onGridChange, peekGrid]);

    const visibleGrid = peekGrid || grid;
    const n = visibleGrid[0]?.length || dim;
    const highlightMap = new Map(
        (highlights || []).map((h) => [`${h.r},${h.c}`, h.kind || 'error'])
    );

    useLayoutEffect(() => {
        const shell = shellRef.current;
        if (!shell || !compactChrome) return undefined;

        const measure = () => {
            const modal = shell.closest('.atelier-puzzle-modal');
            const header = modal?.querySelector('.atelier-puzzle-header');
            const footer = modal?.querySelector('.atelier-puzzle-footer');
            const tools =
                shell.parentElement?.querySelector('.atelier-draw-tools') ||
                modal?.querySelector('.atelier-draw-tools');
            const coach = modal?.querySelector('.atelier-coach');

            const vvW = window.visualViewport?.width ?? window.innerWidth;
            const vvH = window.visualViewport?.height ?? window.innerHeight;
            const landscape =
                window.matchMedia('(orientation: landscape)').matches || vvW > vvH;
            const shortEdge = Math.min(vvW, vvH);
            const phone =
                Boolean(desktopLayout) === false &&
                (modal?.classList.contains('is-phone-play') ||
                    isPhonePuzzleViewport() ||
                    shortEdge < 560);
            const desktop =
                desktopLayout ||
                modal?.classList.contains('is-desktop-play') ||
                (!phone && Math.max(vvW, vvH) >= 700);
            // Phone immersive: landscape always; portrait when 16×16 (dense overlay chrome).
            const immersive =
                !desktop &&
                (modal?.classList.contains('is-immersive') ||
                    Boolean(immersiveLandscape) ||
                    (phone && landscape) ||
                    (phone && n >= 16));

            const gutter = clueGutterFor(n, landscape && !desktop, shortEdge, phone && !desktop);
            setClueGutter((prev) => (prev !== gutter ? gutter : prev));
            const stageGap = n >= 16 ? 0 : STAGE_GAP;
            const edgePad = phone && !desktop ? EDGE_PAD_PHONE : EDGE_PAD;

            // Desktop / wide: size from the card shell with a hard cap — never fill the monitor.
            if (desktop) {
                const fill = shell.getBoundingClientRect();
                const body = modal?.querySelector('.atelier-puzzle-body');
                const bodyBox = body?.getBoundingClientRect();
                let boxW = fill.width || bodyBox?.width || 360;
                if (boxW < 80) boxW = Math.min(360, bodyBox?.width || 360);
                // Modal is height:auto and shrink-wraps the board — do NOT use fill.height
                // (chicken/egg floors at 140). Target the design cap within width + viewport.
                const maxByViewport = Math.floor(vvH * 0.55);
                const stageBudget = Math.floor(
                    Math.min(boxW, desktopBoardCap(n) + gutter + 8, maxByViewport)
                );
                const next = Math.min(
                    desktopBoardCap(n),
                    Math.max(140, stageBudget - gutter - 8)
                );
                setBoardPx((prev) => (Math.abs(prev - next) > 1 ? next : prev));
                return;
            }

            const reportedTop = Math.max(cssVarPx('--vq-hud-top'), cssVarPx('--vq-inset-top'), 0);
            const reportedRight = Math.max(
                cssVarPx('--vq-hud-right'),
                cssVarPx('--vq-inset-right'),
                0
            );
            const reportedBottom = Math.max(
                cssVarPx('--vq-hud-bottom'),
                cssVarPx('--vq-inset-bottom'),
                0
            );
            const reportedLeft = Math.max(cssVarPx('--vq-hud-left'), cssVarPx('--vq-inset-left'), 0);

            // Floors only when CSS vars unset (Telegram lag). Trust positive reported insets.
            const ios =
                /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            const fs = Boolean(window.Telegram?.WebApp?.isFullscreen);
            let floorTop = 0;
            let floorRight = 0;
            let floorBottom = 0;
            let floorLeft = 0;
            if (ios) {
                floorTop = fs ? 112 : 56;
                floorBottom = 34;
                if (landscape) {
                    floorLeft = 48;
                    floorRight = 56;
                    floorTop = Math.max(floorTop, fs ? 64 : 28);
                } else if (fs) {
                    floorRight = 20;
                }
            } else if (fs) {
                floorTop = landscape ? 56 : 64;
                floorRight = landscape ? 96 : 80;
                floorBottom = 16;
                floorLeft = landscape ? 16 : 8;
            } else {
                floorTop = 28;
            }

            const hudTop = reportedTop > 0 ? reportedTop : floorTop;
            const hudRight = reportedRight > 0 ? reportedRight : floorRight;
            const hudBottom = reportedBottom > 0 ? reportedBottom : floorBottom;
            const hudLeft = reportedLeft > 0 ? reportedLeft : floorLeft;

            let chromeX = edgePad * 2 + hudLeft + hudRight;
            let chromeY = edgePad * 2 + hudTop + hudBottom;

            if (immersive) {
                const footerFloating = footer?.classList.contains('is-floating-check');
                if (landscape) {
                    // Overlay header + floating Check — only the DRAW/MARK rail eats width.
                    chromeX += Math.max(48, tools?.getBoundingClientRect().width || TOOL_RAIL);
                } else {
                    // Portrait dense 16: overlay header + floating Check; compact tools row.
                    chromeY += Math.max(
                        TOOL_ROW_DENSE,
                        tools?.getBoundingClientRect().height || TOOL_ROW_DENSE
                    );
                    if (!footerFloating) {
                        chromeY += footer?.getBoundingClientRect().height || 44;
                    }
                }
                if (coach) {
                    const cr = coach.getBoundingClientRect();
                    if (landscape) chromeX += cr.width + 8;
                    else chromeY += cr.height + 8;
                }
            } else {
                chromeY += header?.getBoundingClientRect().height || 36;
                const footerFloating = footer?.classList.contains('is-floating-check');
                if (!footerFloating) {
                    chromeY += footer?.getBoundingClientRect().height || 48;
                }
                if (landscape) {
                    chromeX += Math.max(48, tools?.getBoundingClientRect().width || TOOL_RAIL);
                } else {
                    chromeY += Math.max(TOOL_ROW, tools?.getBoundingClientRect().height || 0);
                }
                if (coach) {
                    const cr = coach.getBoundingClientRect();
                    if (landscape) chromeX += cr.width + 8;
                    else chromeY += cr.height + 8;
                }
            }

            const availW = Math.max(0, vvW - chromeX);
            const availH = Math.max(0, vvH - chromeY);
            let stageBudget = Math.floor(Math.min(availW, availH));

            // Prefer laid-out board shell when flex already reserved chrome (immersive / dense).
            const fill = shell.getBoundingClientRect();
            if (fill.width >= 120 && fill.height >= 120) {
                const shellBudget = Math.floor(Math.min(fill.width, fill.height));
                if (immersive || shellBudget >= stageBudget - 4) {
                    stageBudget = shellBudget;
                }
            }

            const next = stageBudget - gutter - stageGap;
            if (next >= 100) {
                setBoardPx((prev) => (Math.abs(prev - next) > 1 ? next : prev));
            }
        };

        measure();
        requestAnimationFrame(measure);
        const timers = [50, 150, 350, 700].map((ms) => window.setTimeout(measure, ms));
        const ro = new ResizeObserver(measure);
        ro.observe(shell);
        if (shell.parentElement) ro.observe(shell.parentElement);
        const modal = shell.closest('.atelier-puzzle-modal');
        if (modal) ro.observe(modal);
        window.addEventListener('resize', measure);
        window.addEventListener('orientationchange', measure);
        window.visualViewport?.addEventListener('resize', measure);
        const onFs = () => {
            requestAnimationFrame(measure);
            window.setTimeout(measure, 120);
            window.setTimeout(measure, 400);
        };
        window.Telegram?.WebApp?.onEvent?.('fullscreenChanged', onFs);
        window.Telegram?.WebApp?.onEvent?.('fullscreenFailed', onFs);
        return () => {
            timers.forEach((id) => window.clearTimeout(id));
            ro.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('orientationchange', measure);
            window.visualViewport?.removeEventListener('resize', measure);
            window.Telegram?.WebApp?.offEvent?.('fullscreenChanged', onFs);
            window.Telegram?.WebApp?.offEvent?.('fullscreenFailed', onFs);
        };
    }, [n, compactChrome, immersiveLandscape, desktopLayout]);

    useEffect(() => {
        if (!compactChrome) {
            setClueGutter(CLUE_GUTTER);
            return undefined;
        }
        const sync = () => {
            const w = window.visualViewport?.width ?? window.innerWidth;
            const h = window.visualViewport?.height ?? window.innerHeight;
            const landscape =
                window.matchMedia('(orientation: landscape)').matches || w > h;
            const shortEdge = Math.min(w, h);
            setClueGutter(clueGutterFor(n, landscape, shortEdge, shortEdge < 560));
        };
        sync();
        window.addEventListener('resize', sync);
        window.addEventListener('orientationchange', sync);
        return () => {
            window.removeEventListener('resize', sync);
            window.removeEventListener('orientationchange', sync);
        };
    }, [compactChrome, n]);

    const cellFromPoint = useCallback(
        (clientX, clientY) => {
            const board = boardRef.current;
            if (!board) return null;
            const rect = board.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return null;
            const x = clientX - rect.left;
            const y = clientY - rect.top;
            if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
            const cols = visibleGrid[0]?.length || dim;
            const rows = visibleGrid.length || dim;
            const c = Math.min(cols - 1, Math.max(0, Math.floor((x / rect.width) * cols)));
            const r = Math.min(rows - 1, Math.max(0, Math.floor((y / rect.height) * rows)));
            return { r, c };
        },
        [dim, visibleGrid]
    );

    const paintAt = useCallback((r, c, value) => {
        if (value == null) return;
        const key = `${r},${c}`;
        if (lastPainted.current === key) return;
        lastPainted.current = key;
        setGrid((prev) => {
            if (!prev[r] || prev[r][c] === value) return prev;
            const next = prev.map((row) => [...row]);
            next[r][c] = value;
            return next;
        });
    }, []);

    const beginStroke = useCallback(
        (r, c) => {
            const current = gridRef.current[r][c];
            let paint;
            if (mode === CELL.FILLED) {
                paint = current === CELL.FILLED ? CELL.EMPTY : CELL.FILLED;
            } else {
                paint = current === CELL.MARKED ? CELL.EMPTY : CELL.MARKED;
            }
            dragPaintVal.current = paint;
            lastPainted.current = null;
            paintAt(r, c, paint);
        },
        [mode, paintAt]
    );

    const handlePointerDown = (e) => {
        if (!isInteractive || peekGrid) return;
        e.preventDefault();
        const cell = cellFromPoint(e.clientX, e.clientY);
        if (!cell) return;
        isDragging.current = true;
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
        beginStroke(cell.r, cell.c);
    };

    const handlePointerMove = (e) => {
        if (!isDragging.current || !isInteractive || peekGrid) return;
        e.preventDefault();
        const cell = cellFromPoint(e.clientX, e.clientY);
        if (!cell) return;
        paintAt(cell.r, cell.c, dragPaintVal.current);
    };

    const endStroke = (e) => {
        if (!isDragging.current) return;
        isDragging.current = false;
        dragPaintVal.current = null;
        lastPainted.current = null;
        if (e?.currentTarget?.hasPointerCapture?.(e.pointerId)) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
    };

    useEffect(() => {
        const up = () => {
            isDragging.current = false;
            dragPaintVal.current = null;
            lastPainted.current = null;
        };
        window.addEventListener('pointerup', up);
        window.addEventListener('pointercancel', up);
        return () => {
            window.removeEventListener('pointerup', up);
            window.removeEventListener('pointercancel', up);
        };
    }, []);

    if (!clues || !clues.rows || !clues.cols) {
        console.error('[PuzzleGrid] Missing clues:', clues);
        return <div style={{ color: 'red' }}>Error: Invalid Puzzle Data</div>;
    }

    const { rows, cols } = clues;
    const boardStyle = {
        width: boardPx,
        height: boardPx,
        gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${visibleGrid.length || dim}, minmax(0, 1fr))`,
        gap: n >= 16 ? 0 : 1,
    };

    const tools = (
        <div
            className={`atelier-draw-tools${compactChrome ? ' is-underboard' : ''}${
                paintMode ? ` is-guide-${paintMode}` : ''
            }`}
            role="group"
            aria-label="Draw tools"
        >
            <button
                type="button"
                className={`atelier-tool${mode === CELL.FILLED ? ' is-active' : ''}`}
                onClick={() => setMode(CELL.FILLED)}
                aria-pressed={mode === CELL.FILLED}
                title="Draw / carve"
            >
                <span className="atelier-tool-label">DRAW</span>
                <span className="atelier-tool-ico atelier-tool-ico--fill" aria-hidden />
            </button>
            <button
                type="button"
                className={`atelier-tool${mode === CELL.MARKED ? ' is-active' : ''}`}
                onClick={() => setMode(CELL.MARKED)}
                aria-pressed={mode === CELL.MARKED}
                title="Mark vacant"
            >
                <span className="atelier-tool-label">MARK</span>
                <span className="atelier-tool-ico atelier-tool-ico--mark" aria-hidden>
                    <MarkGlyph className="atelier-tool-x" />
                </span>
            </button>
        </div>
    );

    if (!compactChrome) {
        return (
            <div className="puzzle-game-container atelier-puzzle-grid">
                {tools}
                <div className="puzzle-game-board" style={{ maxWidth: 400, width: '100%' }}>
                    <div className="puzzle-row-container">
                        <div className="clue-row-area" style={{ visibility: 'hidden' }} />
                        <div className="clue-col-area">
                            {cols.map((colClues, i) => (
                                <div key={i} className="clue-col-item">
                                    {colClues.map((num, idx) => (
                                        <div key={idx}>{num}</div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="puzzle-row-container">
                        <div className="clue-row-area">
                            {rows.map((rowClues, i) => (
                                <div key={i} className="clue-row-item">
                                    {rowClues.join(' ')}
                                </div>
                            ))}
                        </div>
                        <div
                            ref={boardRef}
                            className={`grid-board-responsive atelier-grid-board${
                                peekGrid ? ' is-peeking' : ''
                            }`}
                            style={{
                                ...boardStyle,
                                width: '100%',
                                height: 'auto',
                                aspectRatio: '1 / 1',
                            }}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={endStroke}
                            onPointerCancel={endStroke}
                        >
                            {visibleGrid.map((row, r) =>
                                row.map((cellState, c) => {
                                    const hint = highlightMap.get(`${r},${c}`);
                                    return (
                                        <div
                                            key={`${r}-${c}`}
                                            className={`grid-cell-responsive${
                                                cellState === CELL.FILLED ? ' filled' : ''
                                            }${cellState === CELL.MARKED ? ' marked' : ''}${
                                                hint ? ` is-hint is-hint--${hint}` : ''
                                            }`}
                                        >
                                            {cellState === CELL.MARKED && <MarkGlyph />}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
                <div className="atelier-grid-legend">
                    <span>
                        <i className="leg-fill" /> Carved
                    </span>
                    <span>
                        <i className="leg-mark" /> Vacant
                    </span>
                    <span>
                        <i className="leg-empty" /> Untouched
                    </span>
                </div>
            </div>
        );
    }

    const stageW = clueGutter + boardPx;
    const stageH = clueGutter + boardPx;

    return (
        <div className="puzzle-game-container atelier-puzzle-grid is-focus">
            <div ref={shellRef} className="atelier-puzzle-board-fill is-measured">
                <div
                    className="atelier-square-stage is-focus"
                    data-grid-n={n}
                    style={{
                        width: stageW,
                        height: stageH,
                        gap: n >= 16 ? 0 : STAGE_GAP,
                        gridTemplateColumns: `${clueGutter}px ${boardPx}px`,
                        gridTemplateRows: `${clueGutter}px ${boardPx}px`,
                    }}
                >
                    <div className="atelier-square-pad" aria-hidden />
                    <div className="clue-col-area">
                        {cols.map((colClues, i) => (
                            <div key={i} className="clue-col-item">
                                {colClues.map((num, idx) => (
                                    <div key={idx}>{num}</div>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="clue-row-area">
                        {rows.map((rowClues, i) => (
                            <div key={i} className="clue-row-item">
                                {rowClues.join(' ')}
                            </div>
                        ))}
                    </div>

                    <div
                        ref={boardRef}
                        className={`grid-board-responsive atelier-grid-board${
                            peekGrid ? ' is-peeking' : ''
                        }`}
                        style={boardStyle}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={endStroke}
                        onPointerCancel={endStroke}
                    >
                        {visibleGrid.map((row, r) =>
                            row.map((cellState, c) => {
                                const hint = highlightMap.get(`${r},${c}`);
                                return (
                                    <div
                                        key={`${r}-${c}`}
                                        className={`grid-cell-responsive${
                                            cellState === CELL.FILLED ? ' filled' : ''
                                        }${cellState === CELL.MARKED ? ' marked' : ''}${
                                            hint ? ` is-hint is-hint--${hint}` : ''
                                        }`}
                                        data-r={r}
                                        data-c={c}
                                    >
                                        {cellState === CELL.MARKED && <MarkGlyph />}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
            {tools}
        </div>
    );
};

export default PuzzleGrid;
